import React, { ReactElement, useEffect, useRef, useState } from 'react';
import './dropdown.module.scss';
import { useOnClickOutside } from '../../../../commonv3/hooks/useOuterClick';
import useDebounce from '../../../../commonv3/hooks/useDebounce';
import { TransitionGroup } from 'react-transition-group';
import { FadeUp } from '../../../../commonv3/components/atoms/animation/Fade';
import classNames from 'classnames';
import { ArrowDropDown, SearchOutlined } from '@material-ui/icons';
import { LoaderRow } from '../layout/LoaderRow';

function createDropdownState(suggestions, suggestionListId) {
    const bodyRef = useRef<HTMLDivElement>();
    const searchInputRef = useRef<HTMLInputElement>();
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resolvedSuggestions, setResolvedSuggestions] = useState([]);
    useOnClickOutside([bodyRef, searchInputRef], () => setShowSuggestions(false));
    const debouncedQuery = useDebounce(query, 200);
    useEffect(
        () => {
            const main = async () => {
                setLoading(true);
                try {
                    const list = await suggestions(debouncedQuery);
                    setResolvedSuggestions(list ? list : []);
                } finally {
                    setLoading(false);
                }
            };
            main();
        },
        [debouncedQuery, suggestionListId]
    );
    return {
        loading,
        bodyRef,
        searchInputRef,
        query,
        setQuery,
        showSuggestions,
        setShowSuggestions,
        resolvedSuggestions,
    };
}

type SuggestionProps<S> = {
    value: S;
    query: string;
    onClick: () => any;
};

type Props<S> = {
    type: 'primary' | 'secondary' | 'danger' | 'white';
    showSearch?: boolean;
    showBottomTool?: boolean;
    suggestions: (query: string) => Promise<S[]> | S[];
    suggestionListId?: string;
    suggestionKey?: (value: S) => string;
    renderSuggestion: (props: SuggestionProps<S>) => ReactElement;
    renderBottomTool?: () => ReactElement;
    onSuggestionClick: (value: S) => any;
    className?: string;
    styleName?: string;
    children: any;
    clickable?: boolean;
};

export function Dropdown<S>(props: Props<S>) {
    const {
        type,
        showSearch = false,
        suggestions,
        suggestionListId,
        suggestionKey,
        renderSuggestion,
        onSuggestionClick,
        showBottomTool,
        renderBottomTool,
        className,
        children,
        clickable = true,
    } = props;
    const {
        query,
        setQuery,
        bodyRef,
        searchInputRef,
        setShowSuggestions,
        showSuggestions,
        resolvedSuggestions,
        loading,
    } = createDropdownState(suggestions, suggestionListId);
    const Suggestion = renderSuggestion;
    return (
        <div styleName={classNames('dropdown', type, { clickable })} className={className}>
            <div
                ref={bodyRef}
                styleName="dropdown-body"
                onClick={() => {
                    if (clickable) {
                        setShowSuggestions(true);
                        setTimeout(() => {
                            searchInputRef.current && searchInputRef.current.focus();
                        }, 100);
                    }
                }}
                data-dropdown="body"
            >
                {children}
                {clickable && <ArrowDropDown styleName={classNames('icon', type)} />}
            </div>
            <div styleName="suggestion-list-wrapper">
                <TransitionGroup>
                    {showSuggestions && (
                        <FadeUp styleName="suggestion-list">
                            <div>
                                {showSearch && (
                                    <div styleName="search-input-wrapper">
                                        <SearchOutlined styleName="search-icon" />
                                        <input
                                            id="dropdown-input"
                                            type="text"
                                            styleName="search-input"
                                            ref={searchInputRef}
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                        />
                                    </div>
                                )}
                                {resolvedSuggestions.map((suggestion, index) => {
                                    const key = suggestionKey ? suggestionKey(suggestion) : index;
                                    return (
                                        <Suggestion
                                            key={key}
                                            value={suggestion}
                                            query={query}
                                            onClick={() => {
                                                setShowSuggestions(false);
                                                onSuggestionClick(suggestion);
                                            }}
                                        />
                                    );
                                })}
                                {loading && <LoaderRow />}
                                {showBottomTool && renderBottomTool && renderBottomTool()}
                            </div>
                        </FadeUp>
                    )}
                </TransitionGroup>
            </div>
        </div>
    );
}

export const SuggestionItem = ({ children, ...props }) => {
    return (
        <div styleName="suggestion-item" {...props}>
            {children}
        </div>
    );
};

export const Highlighter = ({ text, query }) => {
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
        <span>
            {' '}
            {parts.map((part, i) => (
                <span key={i} styleName={part.toLowerCase() === query.toLowerCase() ? 'highlight' : ''}>
                    {part}
                </span>
            ))}{' '}
        </span>
    );
};
